import { NodeProps, useReactFlow } from "reactflow";
import { operationType, selectDataType } from "../../../../../services/types";
import { Main, Styled } from "./style";
import React, { useEffect, useState } from "react";
import { updateNodeValue } from "../../../../../services/method";
import { Textarea } from "../../../../form/textarea";
import { TextInput } from "../../../../form/textInput";
import { Select } from "../../../../form/select";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

type CustomAPIProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function CustomAPI({ id, data, setChildMinimize, setChildSettings }: CustomAPIProps){
   const { setNodes } = useReactFlow();
   const [methodTypes, setMethodTypes] = useState<selectDataType[]>([
      { id: 1, name: 'cURL', refId: 'cURL', isActive: true }, 
      { id: 2, name: 'GET (HTTP)', refId: 'get' }, 
      { id: 3, name: 'POST (HTTP)', refId: 'post' }, 
      { id: 4, name: 'DELETE (HTTP)', refId: 'delete' }, 
      { id: 5, name: 'PUT (HTTP)', refId: 'put' },
      { id: 6, name: 'Let AI Agent Decide', refId: 'agent_decide'}
   ]);
   const [paramsTypes, setParamsTypes] = useState<selectDataType[]>([
      { id: 1, name: 'Query', refId: 'query', isActive: true }, { id: 2, name: 'Body', refId: 'body' }
   ]);
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   useEffect(()=>{
      if (data.value) {
         setMethodTypes((method_types) => method_types.map((method_type) => {
            return { ...method_type, isActive: method_type.refId === data.value.methodType };
         }));
         setParamsTypes((params_types) => params_types.map((params_type) => {
            return { ...params_type, isActive: params_type.refId === data.value.paramsType };
         }));
      } else{
         updateNodeValue(id, setNodes, { paramsType: paramsTypes[0].refId, methodType: methodTypes[0]?.refId });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      const value = e.target.value;
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: value });
   }

   // ---
   const handleSelectRequests = (action: 'methodType' | 'paramsType', selectId: number) => {
      if (action === 'methodType') {
         updateNodeValue(id, setNodes, { methodType: methodTypes.find((method_type)=>method_type.id === selectId)?.refId, paramsType: 'query' });
         setMethodTypes((method_types) => method_types.map((method_type) => {
            if (method_type.id === selectId) return { ...method_type, isActive: true };
            else return { ...method_type, isActive: false };
         }));
         if (selectId === 1) {
            setParamsTypes((params_types) => params_types.map((params_type) => ({ ...params_type, isActive: params_type.refId === 'query' })));
         }
      } else if (action === 'paramsType') {
         updateNodeValue(id, setNodes, { paramsType: paramsTypes.find((params)=> params.id === selectId)?.refId });
         setParamsTypes((params_types) => params_types.map((params_type) => {
            if (params_type.id === selectId) return { ...params_type, isActive: true };
            else return { ...params_type, isActive: false };
         }));
      }
   }

   // ---
   const methodActive = methodTypes.find(m => m.isActive)?.refId;
   // ---
   const warningMessages = React.useMemo(() => {
      const warnings: string[] = [];
      if (methodActive === 'cURL') return warnings;
      const rawUrl = String(data.value?.url || '').trim();
      if (rawUrl) {
         try {
            const parsedUrl = new URL(rawUrl);
            if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
               warnings.push('URL should use http or https scheme.');
            }
            if (parsedUrl.username || parsedUrl.password) {
               warnings.push('Avoid embedding credentials directly in URL.');
            }
         } catch {
            warnings.push('URL appears malformed.');
         }
      }
      const validateJsonField = (label: string, raw: unknown) => {
         const source = String(raw || '').trim();
         if (!source) return;
         try {
            const parsed = JSON.parse(source);
            if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
               warnings.push(`${label} should be a JSON object.`);
               return;
            }
            if (label === 'Headers') {
               for (const [headerKey, headerValue] of Object.entries(parsed as Record<string, unknown>)) {
                  if (/\r|\n/.test(headerKey)) {
                     warnings.push('Header names should not contain newline characters.');
                     break;
                  }
                  if (typeof headerValue === 'string' && /\r|\n/.test(headerValue)) {
                     warnings.push('Header values should not contain newline characters.');
                     break;
                  }
               }
            }
         } catch {
            warnings.push(`${label} must be valid JSON.`);
         }
      };
      validateJsonField('Params', data.value?.params);
      validateJsonField('Headers', data.value?.headers);
      return warnings;
   }, [data.value?.headers, data.value?.params, data.value?.url, methodActive]);
   // ---
   const isWarning = warningMessages.length > 0;
    
   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            style={{ minWidth: '420px' }}
         ><Styled>
            { warningMessages.length ? (
               <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, backgroundColor: '#fff4e5', color: '#8a5b00', fontSize: 12, lineHeight: 1.5 }}>
                  {warningMessages.map((message, index) => (
                     <div key={`${message}-${index}`}>- {message}</div>
                  ))}
               </div>
            ) : null}
            <div>
               <Select 
                  title="Method" data={ methodTypes } handleSelectItem={ (id) => handleSelectRequests('methodType', id) } isSmall={ true } 
               />
               { methodActive !== 'cURL' && <Select 
                  title="Params" 
                  data={ paramsTypes.filter(params => !(params.refId === 'body' && methodTypes.some(method => method.isActive && method.refId === 'get')) ) } 
                  handleSelectItem={ (id) => handleSelectRequests('paramsType', id) } isSmall={ true } 
               /> }
            </div>
            { methodActive !== 'cURL' && <TextInput keyRef="url" value={ data.value?.url } title="URL" placeholder="request_url" handleChange={ handleChangeInput } /> }
            <Textarea
               title={ methodActive === 'cURL' ? '' : "Params"}
               keyRef="params"
               value={ data.value?.params } 
               placeholder= { methodActive === 'cURL' ? 'cURL Command' : "JSON Data"}
               handleChange={ (e) => updateNodeValue(id, setNodes, { params: e.target.value }) } 
            />
            { methodActive !== 'cURL' && <Textarea
               title="Headers"
               keyRef="headers"
               value={ data.value?.headers } 
               placeholder='JSON Data' 
               handleChange={ (e) => updateNodeValue(id, setNodes, { headers: e.target.value }) } 
            /> }
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}